import { useState } from 'react'
import type { ReactNode } from 'react'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import * as Sentry from '@sentry/nextjs'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import Confirmation from 'shared/components/confirmation-modal'
import useUser, { isDomainOwner, isWsAdmin, isWsAssistant } from 'shared/hooks/use-user'
import { useRemoveTopic } from 'modules/community/api/use-remove-topic'
import { useUpdateTopicsOrder } from 'modules/community/api/use-update-topics-order'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import { CommunityWithTopicsInterface } from 'modules/community/types/community-interface'
import { TopicInterface } from 'modules/community/types/topic-interface'
import UpdateTopic from '../../update-topic'
import { TopicItem } from './topic-item'
import { TopicSortableWrapper } from './topic-sortable-wrapper'

export const TopicList = ({ topicItemClassName }: { topicItemClassName?: string }): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const router = useRouter()
  const { communityPath, topicPath } = router.query

  const [editableTopic, setEditableTopic] = useState<TopicInterface | null>(null)
  const [removableTopicId, setRemovableTopicId] = useState<number | null>(null)
  const [activeId, setActiveId] = useState<number | null>(null)
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({})

  const getCommunityWithTopicsQuery = useCommunityWithTopics()
  const updateTopicsOrderMutation = useUpdateTopicsOrder({ communityPath })
  const removeTopicMutation = useRemoveTopic({ topicId: removableTopicId })

  const topics = getCommunityWithTopicsQuery.data?.topics
  const selectedTopic = getCommunityWithTopicsQuery.data?.topics.find(
    topic => topic.path === topicPath,
  )
  const canManageTopics = user && (isDomainOwner(user) || isWsAdmin(user) || isWsAssistant(user))

  const handleDragStart = ({ active }: DragStartEvent): void => {
    setActiveId(active.id as number)
    const node = document.querySelector<HTMLElement>(`[data-topic-id='${active.id}']`)
    if (node) {
      const { width, height } = node.getBoundingClientRect()
      setOverlayStyle({ width, height })
    }
  }

  const handleDragEnd = (event: DragEndEvent): void => {
    if (!getCommunityWithTopicsQuery.data) return
    setActiveId(null)
    const { active, over } = event

    if (active.id !== over?.id) {
      const prevTopics = [...getCommunityWithTopicsQuery.data.topics]
      const oldIndex = prevTopics.findIndex(t => t.id === active.id)
      const index = prevTopics.findIndex(t => t.id === over?.id)
      const topics = arrayMove(prevTopics, oldIndex, index)

      getCommunityWithTopicsQuery.mutate({ ...getCommunityWithTopicsQuery.data, topics }, false)

      updateTopicsOrderMutation
        .trigger({ orderedTopicIds: topics.map(topic => topic.id) })
        .then(data => {
          if (getCommunityWithTopicsQuery.data) {
            getCommunityWithTopicsQuery.mutate(
              {
                ...getCommunityWithTopicsQuery.data,
                topics: data,
              } satisfies CommunityWithTopicsInterface,
              false,
            )
          }
        })
        .catch(error => {
          if (getCommunityWithTopicsQuery.data) {
            getCommunityWithTopicsQuery.mutate(
              {
                ...getCommunityWithTopicsQuery.data!,
                topics: prevTopics,
              } satisfies CommunityWithTopicsInterface,
              false,
            )
          }
          Sentry.captureMessage('Failed to update topic order', {
            level: 'error',
            extra: { error },
          })
        })
    }
  }

  const handleRemoveTopic = async (): Promise<void> => {
    if (!getCommunityWithTopicsQuery.data || !removableTopicId) return
    await removeTopicMutation.trigger()
    const topics = getCommunityWithTopicsQuery.data.topics.filter(t => t.id !== removableTopicId)
    setRemovableTopicId(null)
    await getCommunityWithTopicsQuery.mutate(
      { ...getCommunityWithTopicsQuery.data, topics: topics },
      false,
    )
  }

  if (!topics) return null

  const renderTopics = (): ReactNode => {
    const activeTopic = topics.find(t => t.id === activeId)

    if (canManageTopics) {
      return (
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            strategy={verticalListSortingStrategy}
            items={topics.map(topic => topic.id)}
          >
            <ul className="py-1 lg:max-h-80 lg:overflow-auto">
              {topics.map(topic => (
                <TopicSortableWrapper
                  key={topic.path}
                  id={topic.id}
                  as="li"
                  data-topic-id={topic.id}
                  className={cn(
                    `group flex w-full items-center justify-between py-1 pr-2 pl-1`,
                    topicItemClassName,
                  )}
                >
                  <TopicItem
                    topic={topic}
                    isSelected={selectedTopic?.id === topic.id}
                    communityPath={communityPath}
                    onEdit={setEditableTopic}
                    onDelete={setRemovableTopicId}
                  />
                </TopicSortableWrapper>
              ))}
            </ul>
          </SortableContext>
          <DragOverlay adjustScale={false}>
            {activeTopic ? (
              <div
                className="group flex items-center justify-between rounded-sm bg-transparent p-1"
                style={overlayStyle}
              >
                <TopicItem
                  topic={activeTopic}
                  isSelected={selectedTopic?.id === activeTopic.id}
                  communityPath={communityPath}
                  onEdit={setEditableTopic}
                  onDelete={setRemovableTopicId}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )
    }

    return (
      <ul className="py-2 lg:max-h-80 lg:overflow-auto">
        {topics.map(topic => (
          <li
            key={topic.path}
            className={cn(
              'group flex w-full items-center justify-between px-4 py-1',
              topicItemClassName,
            )}
          >
            <TopicItem
              topic={topic}
              isSelected={selectedTopic?.id === topic.id}
              communityPath={communityPath}
              onEdit={setEditableTopic}
              onDelete={setRemovableTopicId}
            />
          </li>
        ))}
      </ul>
    )
  }

  return (
    <>
      {renderTopics()}
      {editableTopic && (
        <UpdateTopic
          topic={editableTopic}
          onCloseModal={() => setEditableTopic(null)}
          onUpdateSuccess={() => setEditableTopic(null)}
        />
      )}
      <ConfirmationModal
        onConfirm={handleRemoveTopic}
        onCloseModal={() => setRemovableTopicId(null)}
        confirmationContent={t('topics.confirmation.delete')}
        removableTopicId={removableTopicId}
      />
    </>
  )
}

function ConfirmationModal({
  onConfirm,
  onCloseModal,
  confirmationContent,
  removableTopicId,
}: {
  onConfirm: () => void
  onCloseModal: () => void
  confirmationContent: string
  removableTopicId: number | null
}): ReactNode {
  if (!removableTopicId) return null

  return (
    <Confirmation
      onConfirm={onConfirm}
      onCloseModal={onCloseModal}
      confirmationContent={confirmationContent}
    />
  )
}
